import { useState } from 'react';

/**
 * FoundersBadge Component
 * Displays a founders badge for beta users with different size variants
 * 
 * @param {Object} props
 * @param {string} props.size - Badge size: 'small', 'medium', 'large' (default: 'medium')
 * @param {boolean} props.showTooltip - Whether to show tooltip on hover (default: true)
 * @param {string} props.className - Additional CSS classes
 */
export default function FoundersBadge({ 
  size = 'medium', 
  showTooltip = true,
  className = '' 
}) {
  const [isHovered, setIsHovered] = useState(false);
  
  const sizeClasses = {
    small: 'text-[10px] px-1.5 py-0.5 gap-0.5',
    medium: 'text-xs px-2 py-1 gap-1',
    large: 'text-sm px-3 py-1.5 gap-1.5'
  };
  
  const iconSizes = {
    small: 'w-3 h-3',
    medium: 'w-3.5 h-3.5',
    large: 'w-4 h-4'
  };
  
  return (
    <div 
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span 
        className={`
          inline-flex items-center font-semibold rounded-full
          bg-gradient-to-r from-amber-500 to-amber-600
          text-white shadow-sm shadow-amber-500/30
          ${sizeClasses[size]}
        `}
      >
        <span className={iconSizes[size]}>🏆</span>
        <span>Founder</span>
      </span>
      
      {/* Tooltip */}
      {showTooltip && isHovered && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 pointer-events-none">
          <div className="bg-surface-800 border border-surface-700 rounded-lg shadow-xl p-3 text-xs w-48">
            <p className="font-semibold text-amber-400 mb-1">Beta Founder</p>
            <p className="text-surface-400">
              Joined during the beta launch and receives special founder perks.
            </p>
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-surface-800 border-r border-b border-surface-700 rotate-45" />
        </div>
      )}
    </div>
  );
}

/**
 * FoundersBadgeInline Component
 * A simpler inline version for use next to usernames
 */
export function FoundersBadgeInline({ className = '' }) {
  return (
    <span 
      className={`
        inline-flex items-center gap-0.5 text-[10px] font-semibold px-1 py-0.5 rounded
        bg-amber-500/20 text-amber-500
        ${className}
      `}
      title="Beta Founder"
    >
      <span className="w-2.5 h-2.5">🏆</span>
      <span>Founder</span>
    </span>
  );
}

/**
 * FoundersBadgeIcon Component
 * Just the icon, for compact displays like avatars
 */
export function FoundersBadgeIcon({ className = '', size = 'medium' }) {
  const sizes = {
    small: 'w-3 h-3',
    medium: 'w-4 h-4',
    large: 'w-5 h-5'
  };
  
  return (
    <span 
      className={`inline-flex items-center justify-center ${sizes[size]} ${className}`}
      title="Beta Founder"
    >
      🏆
    </span>
  );
}
